import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { useDirectory } from "@/lib/rights";
import { Field, Pick } from "@/components/vendors/NewVendorDialog";

const BLANK = { name: "", ropa_activity_id: "", system_asset_id: "", storage_asset_id: "", trigger: "ACCOUNT_CLOSURE",
  retention_months: 12, legal_basis: "", disposal_method: "DELETE", owner: "" };

export const RuleDialog = ({ open, onOpenChange, meta, rule, onSaved }) => {
  const users = useDirectory();
  const [f, setF] = useState(BLANK);
  const [opts, setOpts] = useState({ activity: [], system: [], storage: [] });
  useEffect(() => {
    if (!open) return;
    setF(rule ? Object.fromEntries(Object.keys(BLANK).map((k) => [k, rule[k] ?? ""])) : BLANK);
    Promise.all(["activity", "system", "storage"].map((k) => api.get("/retention/ropa-options", { params: { kind: k } })))
      .then(([a, s, t]) => setOpts({ activity: a.data.options, system: s.data.options, storage: t.data.options })).catch(() => {});
  }, [open, rule]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const pickActivity = (id) => {
    const a = opts.activity.find((x) => x.id === id);
    setF((x) => ({ ...x, ropa_activity_id: id, name: x.name || (a ? `${a.name} records` : ""),
      retention_months: a?.retention_months || x.retention_months, legal_basis: a?.retention_basis || x.legal_basis,
      system_asset_id: x.system_asset_id || opts.system.find((s) => a?.asset_ids.includes(s.id))?.id || "" }));
  };
  const save = async () => {
    const body = { ...f, retention_months: Number(f.retention_months), owner: f.owner || null };
    try {
      if (rule) await api.patch(`/retention/rules/${rule.id}`, body); else await api.post("/retention/rules", body);
      toast.success(rule ? "Rule updated" : "Rule created as draft");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiError(e, "Could not save rule")); }
  };
  if (!meta) return null;
  const valid = f.name.trim() && f.ropa_activity_id && f.system_asset_id && f.storage_asset_id && Number(f.retention_months) > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="rule-dialog">
        <DialogHeader>
          <DialogTitle>{rule ? `Edit ${rule.rule_id}` : "New retention rule"}</DialogTitle>
          <DialogDescription>Every rule links to a RoPA activity, a system (Application / SaaS) and a storage asset. Picking an activity pre-fills its RoPA retention.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="RoPA activity" className="sm:col-span-2">
            <Pick value={f.ropa_activity_id || undefined} onChange={pickActivity} testId="rule-activity"
                  options={opts.activity.map((a) => [a.id, `${a.name}${a.retention_months ? ` · RoPA ${a.retention_months}m` : ""}`])} />
          </Field>
          <Field label="System (Application / SaaS)"><Pick value={f.system_asset_id || undefined} onChange={set("system_asset_id")} testId="rule-system"
                 options={opts.system.map((a) => [a.id, `${a.name} · ${a.asset_type}`])} /></Field>
          <Field label="Storage asset"><Pick value={f.storage_asset_id || undefined} onChange={set("storage_asset_id")} testId="rule-storage"
                 options={opts.storage.map((a) => [a.id, `${a.name} · ${a.asset_type}`])} /></Field>
          <Field label="Rule name" className="sm:col-span-2"><Input value={f.name} onChange={set("name")} data-testid="rule-name" /></Field>
          <Field label="Trigger"><Pick value={f.trigger} onChange={set("trigger")} options={Object.entries(meta.triggers)} testId="rule-trigger" /></Field>
          <Field label="Retention period (months)"><Input type="number" min={1} value={f.retention_months} onChange={set("retention_months")} className="font-mono" data-testid="rule-months" /></Field>
          <Field label="Legal basis"><Input value={f.legal_basis} onChange={set("legal_basis")} data-testid="rule-basis" /></Field>
          <Field label="Disposal method"><Pick value={f.disposal_method} onChange={set("disposal_method")} options={Object.entries(meta.disposal_methods)} testId="rule-method" /></Field>
          <Field label="Owner"><Pick value={f.owner || undefined} onChange={set("owner")} options={users.map((u) => [u.username, u.full_name || u.username])} testId="rule-owner" /></Field>
        </div>
        {rule?.status === "ACTIVE" && <div className="text-[11px] text-amber-700">Changing the activity, assets, trigger, period or method moves the rule back to Draft, and it needs approval again.</div>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!valid} onClick={save} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="rule-save">{rule ? "Save" : "Create draft"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
